/**
 * Visit log — what THIS visit has looked like, per tab (sessionStorage):
 * the landing page, the referrer, and every page since with dwell + max
 * scroll. Sent with every chat turn (`context.visit`) and every voice
 * page-context push, so the agent opens on what the visitor has actually
 * been doing instead of cold (2026-10-28, slice 2: the agent knew nothing
 * about the visit — no landing page, no pages, no scroll, no dwell).
 *
 * Deliberately tiny: the widget budget is near its ceiling. Dwell counts
 * visible time only; a reload of the same page continues its entry.
 */
import type { VisitSnapshot } from '@showpilot/core';

const KEY = 'sp_visit';
const MAX_PAGES = 10;
type Page = { u: string; d: number; s: number };
type Log = { l: string; r: string; t: number; p: Page[] };

let log: Log | null = null;
/** Start of the current visible stretch (0 = tab hidden). */
let since = 0;
let win: Window | undefined;
let doc: Document | undefined;
let now: () => number = Date.now;

const pathOf = (href: string): string => {
  try {
    const u = new URL(href);
    return u.pathname + u.search;
  } catch {
    return href;
  }
};

function save(): void {
  try {
    win?.sessionStorage.setItem(KEY, JSON.stringify(log));
  } catch {
    /* storage blocked — the log lives in memory for this page */
  }
}

function current(): Page | undefined {
  return log?.p[log.p.length - 1];
}

function flush(): void {
  const cur = current();
  if (cur && since) {
    const t = now();
    cur.d += Math.round((t - since) / 1000);
    since = t;
  }
}

function scrollPct(): number {
  if (!win || !doc) return 0;
  const total = doc.documentElement.scrollHeight || 1;
  return Math.min(100, Math.round(((win.scrollY + win.innerHeight) / total) * 100));
}

function enter(p: string): void {
  if (!log) return;
  if (current()?.u === p) return;
  flush();
  log.p.push({ u: p, d: 0, s: 0 });
  if (log.p.length > MAX_PAGES) log.p.splice(0, log.p.length - MAX_PAGES);
  save();
}

export function installVisitLog(opts?: { win?: Window; doc?: Document; now?: () => number }): void {
  if (log) return;
  win = opts?.win ?? window;
  doc = opts?.doc ?? document;
  now = opts?.now ?? Date.now;
  const href = win.location.href;
  try {
    log = JSON.parse(win.sessionStorage.getItem(KEY) ?? 'null') as Log | null;
  } catch {
    log = null;
  }
  if (!log || !Array.isArray(log.p)) log = { l: pathOf(href), r: doc.referrer || '', t: now(), p: [] };
  since = doc.visibilityState === 'hidden' ? 0 : now();
  enter(pathOf(href));
  win.addEventListener(
    'scroll',
    () => {
      const cur = current();
      if (cur) cur.s = Math.max(cur.s, scrollPct());
    },
    { passive: true },
  );
  doc.addEventListener('visibilitychange', () => {
    if (doc?.visibilityState === 'hidden') {
      flush();
      since = 0;
      save();
    } else {
      since = now();
    }
  });
}

/** SPA navigation: the visitor is on a new page. */
export function visitPageChanged(href: string): void {
  enter(pathOf(href));
}

/** The visit so far, for the server. Null until installed. */
export function visitSnapshot(): VisitSnapshot | null {
  if (!log) return null;
  flush();
  const cur = current();
  if (cur) cur.s = Math.max(cur.s, scrollPct());
  save();
  return {
    landingUrl: log.l,
    ...(log.r ? { referrer: log.r } : {}),
    startedAt: log.t,
    pages: log.p.map((p) => ({ url: p.u, dwellSecs: p.d, scrollPct: p.s })),
  };
}

/** Test seam. */
export function __resetVisitLogForTests(): void {
  log = null;
  since = 0;
}
